import symbolFontUrl from './assets/mata-symboles.woff2?url';
/**
 * A110Z — engraving typefaces: the 30 Google Fonts of the design handoff (constant `FD` of the
 * prototype), in six families (filter Toutes / Minimal / Sérif / Script / Fort / Machine).
 *
 * A110Z2 (David, 6 octobre : « commence en Helvetica de base ») : Helvetica comes first and is the
 * starting font. It is the system face on Apple devices (Helvetica Neue); elsewhere Arimo, a free
 * Google font drawn on the same widths, stands in so the text takes the same room on every phone.
 *
 * One font applies to every text (the logo excepted). `scale` is the prototype's per-font size (its
 * SVG size over the 10-unit base) — kept for reference: since A110Z2 the size is a real capital
 * height in mm, read from each font's own capitals. `letterSpacing` is in em. The fonts are only
 * fetched when the engraving UI opens (`loadFonts`).
 */
export type FontCategory = 'minimal' | 'serif' | 'script' | 'fort' | 'machine';
export type FontFilter = 'all' | FontCategory;

export interface EngraveFont {
  id: string;
  name: string;
  category: FontCategory;
  /** CSS font-family list. */
  family: string;
  weight: number;
  /** The typeface is engraved in capitals. */
  uppercase: boolean;
  /** Size factor relative to the base text height. */
  scale: number;
  /** Letter spacing (em). */
  letterSpacing: number;
  /** Google Fonts family (with weights) for the stylesheet request. */
  google: string;
}

type Row = readonly [string, string, FontCategory, string, number, 0 | 1, number, number, string];

const ROWS: readonly Row[] = [
  ['hellix', 'Hellix', 'minimal', "'Hellix', sans-serif", 400, 0, 10, .3, ''],
  ['tobias', 'Tobias', 'serif', "'Tobias', serif", 500, 0, 10, .3, ''],
  ['helvetica', 'Helvetica', 'minimal', "'Helvetica Neue', Helvetica, Arimo, Arial, sans-serif", 400, 0, 10, .3, 'Arimo:wght@400'],
  ['jost', 'Jost', 'minimal', "'Jost', sans-serif", 400, 0, 9.8, .6, 'Jost:wght@400'],
  ['montserrat', 'Montserrat', 'minimal', "'Montserrat', sans-serif", 500, 0, 9, .5, 'Montserrat:wght@500'],
  ['poppins', 'Poppins', 'minimal', "'Poppins', sans-serif", 500, 0, 9, .4, 'Poppins:wght@500'],
  ['dmsans', 'DM Sans', 'minimal', "'DM Sans', sans-serif", 500, 0, 9.6, .4, 'DM+Sans:wght@500'],
  ['manrope', 'Manrope', 'minimal', "'Manrope', sans-serif", 500, 0, 9.4, .4, 'Manrope:wght@500'],
  ['spacegrotesk', 'Space Grotesk', 'minimal', "'Space Grotesk', sans-serif", 500, 0, 9.4, .3, 'Space+Grotesk:wght@500'],
  ['syne', 'Syne', 'minimal', "'Syne', sans-serif", 700, 0, 9.4, .3, 'Syne:wght@700'],
  ['cormorant', 'Cormorant', 'serif', "'Cormorant Garamond', serif", 600, 1, 10.5, 1.8, 'Cormorant+Garamond:wght@600'],
  ['playfair', 'Playfair Display', 'serif', "'Playfair Display', serif", 500, 0, 10, .3, 'Playfair+Display:wght@500'],
  ['dmserif', 'DM Serif Display', 'serif', "'DM Serif Display', serif", 400, 0, 10.4, .3, 'DM+Serif+Display'],
  ['baskerville', 'Libre Baskerville', 'serif', "'Libre Baskerville', serif", 400, 0, 9, .3, 'Libre+Baskerville'],
  ['bodoni', 'Bodoni Moda', 'serif', "'Bodoni Moda', serif", 500, 0, 10, .3, 'Bodoni+Moda:wght@500'],
  ['ebgaramond', 'EB Garamond', 'serif', "'EB Garamond', serif", 500, 0, 10.6, .3, 'EB+Garamond:wght@500'],
  ['cinzel', 'Cinzel', 'serif', "'Cinzel', serif", 500, 0, 9.4, 1, 'Cinzel:wght@500'],
  ['pinyon', 'Pinyon Script', 'script', "'Pinyon Script', cursive", 400, 0, 14, 0, 'Pinyon+Script'],
  ['greatvibes', 'Great Vibes', 'script', "'Great Vibes', cursive", 400, 0, 14, 0, 'Great+Vibes'],
  ['dancing', 'Dancing Script', 'script', "'Dancing Script', cursive", 500, 0, 12.5, 0, 'Dancing+Script:wght@500'],
  ['parisienne', 'Parisienne', 'script', "'Parisienne', cursive", 400, 0, 13, 0, 'Parisienne'],
  ['allura', 'Allura', 'script', "'Allura', cursive", 400, 0, 15, 0, 'Allura'],
  ['sacramento', 'Sacramento', 'script', "'Sacramento', cursive", 400, 0, 15, 0, 'Sacramento'],
  ['caveat', 'Caveat', 'script', "'Caveat', cursive", 500, 0, 13, 0, 'Caveat:wght@500'],
  ['bebas', 'Bebas Neue', 'fort', "'Bebas Neue', sans-serif", 400, 1, 12, 1, 'Bebas+Neue'],
  ['anton', 'Anton', 'fort', "'Anton', sans-serif", 400, 1, 10.5, .6, 'Anton'],
  ['abril', 'Abril Fatface', 'fort', "'Abril Fatface', serif", 400, 0, 10.5, .3, 'Abril+Fatface'],
  ['alfaslab', 'Alfa Slab One', 'fort', "'Alfa Slab One', serif", 400, 0, 9.4, .3, 'Alfa+Slab+One'],
  ['archivoblack', 'Archivo Black', 'fort', "'Archivo Black', sans-serif", 400, 0, 9, .3, 'Archivo+Black'],
  ['bungee', 'Bungee', 'fort', "'Bungee', sans-serif", 400, 1, 8.6, .4, 'Bungee'],
  ['courier', 'Courier Prime', 'machine', "'Courier Prime', monospace", 400, 0, 10, .3, 'Courier+Prime'],
  ['spacemono', 'Space Mono', 'machine', "'Space Mono', monospace", 400, 0, 9, .2, 'Space+Mono'],
  ['plexmono', 'IBM Plex Mono', 'machine', "'IBM Plex Mono', monospace", 500, 0, 9.4, .2, 'IBM+Plex+Mono:wght@500'],
];

export const ENGRAVE_FONTS: readonly EngraveFont[] = ROWS.map(([id, name, category, family, weight, upper, size, ls, google]) => ({
  id, name, category, family, weight, uppercase: upper === 1, scale: size / 10, letterSpacing: ls / size, google,
}));

export const DEFAULT_FONT = 'hellix';

export const FONT_FILTERS: readonly FontFilter[] = ['all', 'minimal', 'serif', 'script', 'fort', 'machine'];

/**
 * A110Z3: symbols are drawn by « Mata Symboles » (tools/engrave/symbols_font.py), bundled with the try-on. It maps
 * only the 28 symbols and U+FE0E, so listed FIRST it draws the symbols and lets every letter, digit and space fall
 * through to the engraving typeface (`fontStack`).
 */
export const SYMBOL_FAMILY = "'Mata Symboles'";

/** CSS font-family of an engraving: our symbols first, then the typeface. */
export function fontStack(font: EngraveFont): string {
  return `${SYMBOL_FAMILY}, ${font.family}`;
}

export function fontById(id: string): EngraveFont {
  return ENGRAVE_FONTS.find(f => f.id === id) ?? ENGRAVE_FONTS.find(f => f.id === DEFAULT_FONT)!;
}

export function fontsIn(filter: FontFilter): EngraveFont[] {
  return ENGRAVE_FONTS.filter(f => filter === 'all' || f.category === filter);
}

/** Google Fonts stylesheet for every engraving font (the symbols come with the try-on, not from Google). */
export function fontsStylesheetUrl(): string {
  const families = ENGRAVE_FONTS.map(f => f.google).filter(Boolean);
  return `https://fonts.googleapis.com/css2?${families.map(f => `family=${f}`).join('&')}&display=swap`;
}

const fontAssets = import.meta.glob<string>('./assets/fonts/*.woff2', { eager: true, query: '?url', import: 'default' });
let linkInstalled = false;
let symbolsInstalled: Promise<void> | null = null;
const loaded = new Set<string>();

/** Registers « Mata Symboles » once (FontFace API; fetched only when the engraving opens). */
export function installSymbolFont(): Promise<void> {
  if (symbolsInstalled) return symbolsInstalled;
  symbolsInstalled = (async () => {
    try {
      const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
      if (!fonts || typeof FontFace !== 'function') return;
      const face = new FontFace('Mata Symboles', `url(${symbolFontUrl}) format('woff2')`, { display: 'block' });
      fonts.add(face);
      await face.load();
    } catch {
      // Without it the symbols fall back to the system's own (still text presentation).
    }
  })();
  return symbolsInstalled;
}

/** Adds the stylesheet once (lazily: only a customer who opens Gravure fetches the fonts). */
export function installFontsStylesheet(doc: Document = document): void {
  void installSymbolFont();
  if (linkInstalled) return;
  linkInstalled = true;
  if (doc.querySelector('[data-mt-engrave-fonts]')) return;
  const style = doc.createElement('style'); style.dataset.mtEngraveFonts = '1';
  style.textContent = ENGRAVE_FONTS.map(font => {
    const family = font.id === 'helvetica' ? 'Arimo' : font.family.split(',')[0]!.replace(/['"]/g, '');
    return `@font-face {font-family:'${family}';font-weight:${font.weight};font-style:normal;font-display:swap;src:url('${fontAssets[`./assets/fonts/${font.id}.woff2`]}');}`;
  }).join('\n');
  doc.head.append(style);
}

/**
 * Resolves once the font (and the symbol fonts) are usable, so the temple preview can be
 * measured and drawn with the real glyphs. Never rejects, never waits longer than `timeoutMs`.
 */
export async function loadFont(font: EngraveFont, timeoutMs = 4000): Promise<void> {
  const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
  if (!fonts) return;
  const key = font.id;
  if (loaded.has(key)) return;
  const timeout = new Promise<void>(resolve => window.setTimeout(resolve, timeoutMs));
  try {
    installFontsStylesheet();
    const result = await Promise.race([Promise.all([fonts.load(`${font.weight} 16px ${font.family}`), installSymbolFont()]), timeout]);
    if (result && result[0].length > 0 && result[0].every(face => face.status === 'loaded')) loaded.add(key);
  } catch {
    // A font that cannot load leaves the fallback of its family list.
  }
}

/** For tests and the debug page. */
export function _resetFontLoading(): void {
  linkInstalled = false;
  symbolsInstalled = null;
  loaded.clear();
}
